import { createApp } from 'vue';
import Inliner from '@thehotelsnetwork/personalization-inliner/dist/inliner.es';
import css from '@thehotelsnetwork/personalization-inliner/dist/inliner.styles';
import { AbortablePromise, AbortError, findMountElement } from './main.js';
import { createRootContainer, insertRootContainer } from './chunk-insert-root-container.js';
import { createPcomponentParam } from './chunk-create-pcomponent-param.js';
import './chunk-widgets.js';
import '@thehotelsnetwork/md5';

const factory = ({ document, thn, }) => {
    const viewData = {};
    const widgetView = {
        data: viewData,
        hasFailed() {
            return !!viewData.error;
        },
        async create(ctx) {
            if (viewData.modal) {
                return;
            }
            const { bundle, debugPrefix } = ctx;
            const zIndex = bundle.zIndex ? String(-1 * Number(bundle.zIndex)) : 'auto';
            const styleProps = {
                'background-color': 'transparent',
                display: 'block',
                float: 'none',
                margin: '0',
                padding: '0',
                border: '0',
                position: 'fixed',
                top: '0',
                left: '0',
                right: '0',
                visibility: 'hidden',
                'z-index': zIndex,
            };
            // This container if for the "modal"
            const { shadowEl: shadowModal, topElement: modalTopElement, shadowRoot: modalShadowRoot, } = createRootContainer({
                id: this.data.id,
                // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment
                contentStyles: css,
                topElementStyles: styleProps,
            }, { document });
            viewData.modal = modalTopElement;
            viewData.modalShadowRoot = modalShadowRoot;
            viewData.shadowModal = shadowModal;
            modalTopElement.setAttribute('data-thn-visible', '0');
            // attach modal to document
            const insertArgs = document.body.lastElementChild
                ? [document.body.lastElementChild, modalTopElement, 'IA']
                : [document.body, modalTopElement, 'II'];
            const isRootInserted = insertRootContainer(...insertArgs);
            if (!isRootInserted) {
                const error = new Error('Failed to insert the root container in the DOM');
                this.logger.warn(debugPrefix, error.message, 'with args:', insertArgs);
                error.status = 'no-selector';
                viewData.error = error;
                throw error;
            }
        },
        async show(ctx) {
            if (!viewData.modal || !viewData.shadowModal) {
                await widgetView.create.call(this, ctx);
            }
            if (!viewData.modal || !viewData.shadowModal) {
                return;
            }
            if (viewData.error && viewData.error.status) {
                widgetView.destroy();
                return;
            }
            const { bundle, debugPrefix } = ctx;
            const fontsData = bundle.tool.getFontsData();
            if (fontsData) {
                const fontsLoader = await this.container.async.fontsLoader;
                const fontsPromise = AbortablePromise.from(fontsLoader.addFonts(fontsData, document));
                thn.pageUnload(() => fontsPromise.abort());
                try {
                    await fontsPromise;
                }
                catch (error) {
                    if (error instanceof AbortError) {
                        return;
                    }
                    throw error;
                }
            }
            viewData.modal.style.visibility = 'visible';
            viewData.modal.setAttribute('data-thn-visible', '1');
            const zIndex = Number(viewData.modal.style.zIndex);
            if (zIndex < 0) {
                viewData.modal.style.zIndex = `${-1 * zIndex}`;
            }
            bundle.modalRootContainer = viewData.shadowModal || viewData.modal;
            const id = `${this.data.name}-${bundle.tool.getId()}`;
            const mountSelector = bundle.tool.getMountSelector();
            const options = { checkId: id, document };
            // First try: search the position in this document
            const reference = findMountElement(mountSelector, options);
            if (!reference) {
                const selector = mountSelector || 'no selector';
                const error = new Error(`Unable to find the target container "${selector}"`);
                error.status = 'no-selector';
                throw error;
            }
            const styleProps = {
                'background-color': 'transparent',
                border: 0,
                display: 'block',
                float: 'none',
                margin: 0,
                padding: 0,
                position: 'relative',
                // NOTE: general bundle.zIndex is only used for the "modal" element
                'z-index': 0,
            };
            const { shadowEl, topElement } = createRootContainer({
                id,
                // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment
                contentStyles: css,
                topElementStyles: styleProps,
            }, { document });
            const position = bundle.tool.getPosition() || '';
            const isRootInserted = insertRootContainer(reference, topElement, position);
            if (!isRootInserted) {
                this.logger.warn(debugPrefix, 'Failed to inject root container in the DOM with data:', { reference, element: topElement, position: bundle.devicePosition });
                const error = new Error('Unable to insert the root container in the DOM');
                error.status = 'no-selector';
                throw error;
            }
            // Create the vue app
            await this.container.scheduler.yield();
            const app = createApp(Inliner, createPcomponentParam(bundle));
            app.mount(shadowEl);
            // hide on page unloading
            this.onDestroy(() => {
                app.unmount();
                topElement.remove();
            });
        },
        destroy() {
            if (viewData.modal) {
                viewData.modal.remove();
                delete viewData.modal;
                delete viewData.shadowModal;
                delete viewData.modalShadowRoot;
            }
        },
    };
    return widgetView;
};

export { factory };
